import { type Student } from "../providers/studentsProvider";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { maxValue, minValue, NumberInput, useRefresh, useUpdate } from "react-admin";
import { FieldValues, useWatch } from "react-hook-form";
import type { DialogProps } from "../misc/DialogProps";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { Form2 } from "../misc/Form2";
import { Column } from "../misc/Column";
import { SelectInput } from "../inputs/SelectInput";
import { BooleanInput } from "../inputs/BooleanInput";
import { DateInput } from "../inputs/DateInput";
import { DateTime } from "luxon";
import { formatMoney } from "../misc/Money";
import { SaveButton2 } from "../buttons/SaveButton2";
import { SubscriptionPackageDto } from "../api/server.api";

export function EditSubscriptionPackageDialog(
  props: { student: Student; subscriptionPackages: Array<SubscriptionPackageDto> } & DialogProps,
) {
  const { student, subscriptionPackages, ...dialogProps } = props;
  return (
    <Dialog {...dialogProps} maxWidth="sm" fullWidth>
      <DialogTitle>AUTOVIO Pro Paket bearbeiten</DialogTitle>
      <DialogCloseButton onClick={dialogProps.onClose} />
      <_Form student={student} subscriptionPackages={subscriptionPackages} closeDialog={dialogProps.onClose} />
    </Dialog>
  );
}

interface FormValues {
  subscriptionPackageId?: string;
  subscriptionPackageDisabled: boolean;
  subscriptionPackagePausedUntil: DateTime | null;
  subscriptionRenewalDay?: number;
}

function _Form(props: {
  student: Student;
  subscriptionPackages: Array<SubscriptionPackageDto>;
  closeDialog: () => void;
}) {
  const { student, subscriptionPackages, closeDialog } = props;
  const refresh = useRefresh();
  const [update] = useUpdate();

  const onSubmit = async (formData: FieldValues) => {
    const data = formData as FormValues;
    try {
      const selectedPackage = subscriptionPackages.find((pkg) => pkg.id === data.subscriptionPackageId);
      const updateData: Partial<Student> = {
        ...(data.subscriptionPackageId ? { subscriptionPackageId: selectedPackage?.id } : {}),
        subscriptionPackageDisabled: data.subscriptionPackageDisabled,
        subscriptionPackagePausedUntil: data.subscriptionPackagePausedUntil,
        ...(data.subscriptionRenewalDay !== undefined ? { subscriptionRenewalDay: data.subscriptionRenewalDay } : {}),
      };

      await update("students", {
        id: student.id,
        data: updateData,
        previousData: student,
      });

      showSuccessToast("AUTOVIO Pro Paket erfolgreich aktualisiert.");
      refresh();
      closeDialog();
    } catch (error) {
      showErrorToast("Fehler beim Aktualisieren des AUTOVIO Pro Pakets.");
      console.error(error);
    }
  };
  return (
    <Form2 onSubmit={onSubmit}>
      <DialogContent>
        <_FormContent subscriptionPackages={subscriptionPackages} />
      </DialogContent>
      <DialogActions>
        <SaveButton2 />
      </DialogActions>
    </Form2>
  );
}

function _FormContent(props: { subscriptionPackages: Array<SubscriptionPackageDto> }) {
  const { subscriptionPackages } = props;
  const subscriptionPackageDisabled = useWatch({ name: "subscriptionPackageDisabled" });

  const subscriptionPackageOptions: Array<[string, string]> = subscriptionPackages.map((pkg) => [
    pkg.id,
    `${pkg.name} (${formatMoney({ amount: Number(pkg.price.amount), currency: pkg.price.currency })} / Monat)`,
  ]);

  return (
    <Column spacing={2}>
      <SelectInput source="subscriptionPackageId" label="AUTOVIO Pro Paket" options={subscriptionPackageOptions} />
      <DateInput
        source="subscriptionPackagePausedUntil"
        label="Pausiert bis"
        allowPastDates={false}
        required={false}
        disabled={subscriptionPackageDisabled}
        showClearButton
        validate={(value) => {
          if (value && value < DateTime.now()) {
            return "Bitte wählen ein Datum in der Zukunft aus.";
          }
          return true;
        }}
      />
      <BooleanInput source="subscriptionPackageDisabled" label="Abonnement deaktiviert" />
      <NumberInput
        source="subscriptionRenewalDay"
        label="Zahlungstag"
        validate={[minValue(1, "Muss zwischen 1 und 28 liegen."), maxValue(28, "Muss zwischen 1 und 28 liegen.")]}
      />
    </Column>
  );
}
